/* MIT License

Copyright (c) 2025 Brad Schick

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE. */
import { getSodium } from './crypto';
import * as cc from './cipher.consts';
import { numToBytes, BYOBStreamReader, ensureArrayBuffer, concatArrays, logError } from './utils';

import { Decipher, CipherState, Extractor, type CipherDataInfo } from './ciphers-current';
import type { KeyProvider } from './keys';

export class DecipherV1 extends Decipher {
   /* V1 CipherData Layout (it was a bit brain-dead, but it wasn't written to files)
      <Document>
         MAC_BYTES
         ALG_BYTES
         IV_BYTES (variable)
         SLT_BYTES
         IC_BYTES
         VER_BYTES
         HINT_SIZE_BYTES
         HINT_BYTES (variable)
         ENC_DATA_BYTES (variable)
      </Document>
   */

   private _headerish?: Uint8Array;

   constructor(keyProvider: KeyProvider, reader: BYOBStreamReader, headerish?: Uint8Array) {
      super(keyProvider, reader);

      // V1 didn't really have a header, save the data to combine
      // with the rest of the stream for decoding
      this._headerish = headerish;
   }

   public override protocolVersion(): number {
      return cc.VERSION1;
   }

   // For V1, this should be the entire CipherData array
   protected override async _decodeBlock0Impl(): Promise<void> {
      try {
         // This isn't very efficient, but it simplifies object creation and V4 logic
         // (which are more important)
         let [payload] = await this._reader.readFill(new ArrayBuffer(cc.PAYLOAD_SIZE_MAX));

         if (this._headerish) {
            payload = concatArrays([this._headerish, payload]);
         }

         // V1 should test be larger, but this get simple cases
         if (payload.byteLength < cc.PAYLOAD_SIZE_MIN) {
            throw new Error(`Invalid paysload size1: ${payload.byteLength}`);
         }

         // Need to treat all values an UNTRUSTED since the signature has not yet been
         // validated, Extractor does test each value for valid ranges as we unpack
         const extractor = new Extractor(payload);

         // Order must be invariant (as oringally laid out in v1)
         const mac = extractor.mac;
         const alg = extractor.alg;
         const iv = extractor.iv;
         const slt = extractor.slt;
         const ic = extractor.ic;
         const ver = extractor.ver;
         if (ver !== cc.VERSION1) {
            throw new Error(`Invalid version of: ${ver}`);
         }
         const encryptedHint = extractor.hint;
         const encryptedData = extractor.remainder('edata');

         // Repack because we don't have the contiguous data any longer
         const fileAD = DecipherV1._encodeAD({
            alg,
            iv,
            ver,
            ic,
            slt,
            encryptedHint,
         });

         this._blockData = {
            mac,
            ver,
            payloadSize: payload.byteLength,
            flags: 0,
            alg,
            iv,
            encryptedData,
            additionalData: fileAD,
         };

         const cdInfo: CipherDataInfo = {
            ver,
            alg,
            ic,
            lp: 1,
            lpEnd: 1,
            slt,
         };
         this._keyProvider.setCipherDataInfo(cdInfo);

         // Avoiding the Doom Principle and verify signature before crypto operations.
         // Aka, check MAC as soon as possible after we  have the signing key and data.
         await this._verifyMAC();

         if (encryptedHint!.byteLength !== 0) {
            const [hk, hIV] = await this._keyProvider.getHintCipherKeyAndIV(iv);
            const hintBytes = await Decipher._doDecrypt(alg, hk, hIV, encryptedHint);
            this._keyProvider.setHint(new TextDecoder().decode(hintBytes));
         }

         this._state = CipherState.Block0Decoded;
      } catch (err) {
         this.errorState();
         logError(err);
         throw err;
      } finally {
         this._headerish = undefined;
      }
   }

   private async _verifyMAC(): Promise<void> {
      if (!this._blockData?.additionalData || !this._blockData.encryptedData || !this._blockData) {
         throw new Error('Invalid MAC data');
      }

      const data = concatArrays([this._blockData.additionalData, this._blockData.encryptedData]);

      const sk = await this._keyProvider.getSigningKey();

      // V1 uses HMAC from webcrypto
      let subtleSK: CryptoKey | undefined = await crypto.subtle.importKey(
         'raw',
         sk,
         { name: 'HMAC', hash: 'SHA-256', length: 256 },
         false,
         ['verify'],
      );

      const valid: boolean = await crypto.subtle.verify('HMAC', subtleSK, this._blockData.mac, data);
      subtleSK = undefined;
      if (valid) {
         return;
      }

      throw new Error('Invalid HMAC signature');
   }

   public override async decryptBlockN(): Promise<Uint8Array> {
      if (this._state !== CipherState.Block0Done) {
         throw new Error('Decipher block0 not complete');
      }
      this.finishedState();

      // This is the signal decrytion is done. V1 never has more than block0
      return new Uint8Array();
   }
}

export class DecipherV4 extends Decipher {
   /* V4 CipherData Layout (hopefully less brain dead). Tags are just notation...
    * and are not actually in the data stream. All encodings have one block0 instance
    * followed by zero or more blockN instances

      <Document>
         <Block0>
            <Header>
               MAC_BYTES - 32
               VER_BYTES - 2
               PAYLOAD_SIZE_BYTES - 4
            </Header>
            <Payload>
               <Additional Data>
                  ALG_BYTES - 2
                  IV_BYTES (variable) - [12, 24, 32]
                  SLT_BYTES - 16
                  IC_BYTES - 4
                  LPP_BYTES (packed lp and lpEnd) - 1
                  EHINT_LEN_BYTES - 1
                  EHINT_BYTES (variable) - [0-255]
               </Additional Data>
               <Encrypted Data>
                  EDATA_BYTES (variable)
               </Encrypted Data>
            </Payload>
         </Block0>
         <BlockN>
            <Header>
               MAC_BYTES
               VER_BYTES
               PAYLOAD_SIZE_BYTES
            </Header>
            <Payload>
               <Additional Data>
                  ALG_BYTES
                  IV_BYTES (variable)
               </Additional Data>
               <Encrypted Data>
                  EDATA_BYTES (variable)
               </Encrypted Data>
            </Payload>
         </BlockN>
         ...
      </Document>
   */

   private _header?: Uint8Array;

   constructor(keyProvider: KeyProvider, reader: BYOBStreamReader, header?: Uint8Array) {
      super(keyProvider, reader);
      this._header = header;
   }

   public override protocolVersion(): number {
      return cc.VERSION4;
   }

   private async _decodeHeader(header?: Uint8Array): Promise<boolean> {
      // Need to treat all values an UNTRUSTED since the signature has not yet been
      // validated.
      let done: boolean = true;
      if (!header) {
         [header, done] = await this._reader.readFill(new ArrayBuffer(cc.HEADER_BYTES_OLD));
      } else if (header && header.byteLength < cc.HEADER_BYTES_OLD) {
         // This isn't very efficient, but it simplifies object creation and current version logic
         // (which are more important)
         let missed: Uint8Array;
         [missed, done] = await this._reader.readFill(new ArrayBuffer(cc.HEADER_BYTES_OLD - header.byteLength));
         header = concatArrays([header, missed]);
      }

      // This signals successful completion of block reads
      if (header.byteLength === 0) {
         return true;
      }
      if (done || header.byteLength < cc.HEADER_BYTES_OLD) {
         this._reader.cleanup();
         throw new Error('Missing cipher data header');
      }

      const extractor = new Extractor(header);

      // Order must be invariant (extractor validates sizes and ranges)
      const mac = extractor.mac;
      const ver = extractor.ver;
      if (ver !== this.protocolVersion()) {
         throw new Error(`Invalid version of: ${ver}`);
      }
      const payloadSize = extractor.size;

      // V4 did not use flags, but had extra byte in payloadSize. Extract to move offset
      const flags = extractor.flags;

      this._blockData = {
         mac: ensureArrayBuffer(mac),
         ver,
         payloadSize,
         flags,
      };

      return false;
   }

   // Does not allow encoding with zero length encrypted text since that
   // is not needed.
   protected override async _decodeBlock0Impl(): Promise<void> {
      try {
         await this._decodeHeader(this._header);
         if (!this._blockData) {
            throw new Error('Data not initialized');
         }

         // Need to treat all values an UNTRUSTED since the signature has not yet been
         // validated, Extractor does test each value for valid ranges as we unpack
         const [payload, done] = await this._reader.readFill(new ArrayBuffer(this._blockData.payloadSize));
         if (done) {
            this._reader.cleanup();
         }

         if (payload.byteLength !== this._blockData.payloadSize) {
            throw new Error(`Cipher data length mismatch1: ${payload.byteLength}`);
         }

         const extractor = new Extractor(payload);

         // Order must be invariant
         this._blockData.alg = extractor.alg;
         this._blockData.iv = extractor.iv;
         const slt = extractor.slt;
         const ic = extractor.ic;
         const [lp, lpEnd] = extractor.lpp();
         const encryptedHint = extractor.hint;
         this._blockData.encryptedData = extractor.remainder('edata');

         // V4 additional data is the payload minus encrypted data
         this._blockData.additionalData = payload.subarray(
            0,
            extractor.offset - this._blockData.encryptedData.byteLength,
         );

         const cdInfo: CipherDataInfo = {
            ver: this._blockData.ver,
            alg: this._blockData.alg,
            ic,
            lp,
            lpEnd,
            slt,
         };
         this._keyProvider.setCipherDataInfo(cdInfo);

         // Avoiding the Doom Principle and verify signature before crypto operations.
         // Aka, check MAC as soon as possible after we have the signing key and data.
         await this._verifyMAC();

         if (encryptedHint!.byteLength !== 0) {
            const [hk, hIV] = await this._keyProvider.getHintCipherKeyAndIV(this._blockData.iv);
            const hintBytes = await Decipher._doDecrypt(this._blockData.alg, hk, hIV, encryptedHint);
            this._keyProvider.setHint(new TextDecoder().decode(hintBytes));
         }

         this._state = CipherState.Block0Decoded;
      } catch (err) {
         this.errorState();
         logError(err);
         throw err;
      } finally {
         this._header = undefined;
      }
   }

   public override async decryptBlockN(): Promise<Uint8Array> {
      try {
         if (this._state !== CipherState.Block0Done) {
            throw new Error(`Decipher invalid state ${this._state}`);
         }

         // This does MAC check
         await this._decodeBlockN();

         //@ts-expect-error
         if (this._state === CipherState.Finished) {
            // this is the signal that decryption is complete
            return new Uint8Array(0);
         }

         this._multiBlock = true;

         if (!this._blockData?.alg || !this._blockData.iv || !this._blockData.encryptedData) {
            throw new Error('Data not initialized');
         }

         const ek = await this._keyProvider.getCipherKey(false);
         const decrypted = await Decipher._doDecrypt(
            this._blockData.alg,
            ek,
            this._blockData.iv,
            this._blockData.encryptedData,
            this._blockData.additionalData,
         );

         // Occurs when the last block was only present to mark termination (in v5+)
         if (decrypted.byteLength === 0) {
            await this._verifyEmptyReader();
            this.finishedState();
         }

         return decrypted;
      } catch (err) {
         this.errorState();
         logError(err);
         throw err;
      } finally {
         this._blockData = undefined;
      }
   }

   // Importers of CipherService should not need this function directly,
   // but it is public for unit testing. Does not allow encoding
   // with zero length encrypted text since that is not needed
   protected async _decodeBlockN(): Promise<void> {
      // Need to treat all values an UNTRUSTED since the signature has not yet been
      // validated, Extractor does test each value for valid ranges as we unpack
      try {
         const done = await this._decodeHeader();
         if (done) {
            this.finishedState();
            return;
         }

         if (!this._blockData) {
            throw new Error('Data not initialized');
         }

         // Don't need to look at done for the fill since there will will be another
         // call and the next header will report done.
         const [payload] = await this._reader.readFill(new ArrayBuffer(this._blockData.payloadSize));

         if (payload.byteLength !== this._blockData.payloadSize) {
            throw new Error(`Cipher data length mismatch2: ${payload.byteLength}`);
         }

         const extractor = new Extractor(payload);

         // Order must be invariant
         this._blockData.alg = extractor.alg;
         this._blockData.iv = extractor.iv;
         this._blockData.encryptedData = extractor.remainder('edata');

         // V4 additional data is payload - encrypted data
         this._blockData.additionalData = payload.subarray(
            0,
            extractor.offset - this._blockData.encryptedData.byteLength,
         );

         // Avoiding the Doom Principle and verify signature before crypto operations.
         // Aka, check MAC as soon as possible after we  have the signing key and data.
         await this._verifyMAC();
      } catch (err) {
         this.errorState();
         logError(err);
         throw err;
      }
   }

   protected async _verifyMAC(): Promise<void> {
      if (
         !this._blockData?.payloadSize ||
         !this._blockData.ver ||
         !this._blockData.additionalData ||
         !this._blockData.encryptedData ||
         !this._blockData.mac
      ) {
         throw new Error('Data not initialized');
      }

      const encVer = numToBytes(this._blockData.ver, cc.VER_BYTES);
      const encSizeBytes = numToBytes(this._blockData.payloadSize, cc.PAYLOAD_SIZE_BYTES + cc.FLAGS_BYTES);

      const sodium = getSodium();
      const sk = await this._keyProvider.getSigningKey();
      const state = sodium.crypto_generichash_init(sk, cc.MAC_BYTES);
      sodium.crypto_generichash_update(state, encVer);
      sodium.crypto_generichash_update(state, encSizeBytes);
      sodium.crypto_generichash_update(state, this._blockData.additionalData);
      sodium.crypto_generichash_update(state, this._blockData.encryptedData);

      const testMac = sodium.crypto_generichash_final(state, cc.MAC_BYTES);
      const validMac: boolean = sodium.memcmp(this._blockData.mac, testMac);

      if (validMac) {
         return;
      }

      throw new Error('Invalid MAC signature');
   }
}

export class DecipherV5 extends DecipherV4 {
   private _lastMac?: Uint8Array<ArrayBufferLike> = new Uint8Array(0);
   private _lastFlags = 0;

   public override protocolVersion(): number {
      return cc.VERSION5;
   }

   protected override _purge() {
      if (this._lastMac) {
         this._lastMac.fill(0);
         this._lastMac = undefined;
      }
      super._purge();
   }

   protected override async _decodeBlock0Impl(): Promise<void> {
      await super._decodeBlock0Impl();

      // Eventually flags may be a bitfield
      if (this._state === CipherState.Finished && this._lastFlags !== 1) {
         throw new Error('Missing terminal data block');
      }

      if (this._blockData) {
         this._lastFlags = this._blockData.flags!;
      }
   }

   protected override async _decodeBlockN(): Promise<void> {
      await super._decodeBlockN();

      // If we loaded more data, and lastFlags was 1 (change to bitfield someday)
      // we have an error
      if (this._lastFlags === 1 && this._state !== CipherState.Finished) {
         throw new Error(`Terminal block already read ${this._state}`);
      }

      // Eventually flags may be a bitfield
      if (this._state === CipherState.Finished && this._lastFlags !== 1) {
         throw new Error('Missing terminal data block');
      }

      if (this._blockData) {
         this._lastFlags = this._blockData.flags!;
      }
   }

   protected override async _verifyMAC(): Promise<void> {
      if (
         !this._blockData?.payloadSize ||
         !this._blockData.ver ||
         !this._blockData.additionalData ||
         !this._blockData.encryptedData ||
         !this._blockData.mac ||
         !this._lastMac
      ) {
         throw new Error('Data not initialized');
      }

      const encVer = numToBytes(this._blockData.ver, cc.VER_BYTES);
      const encSizeBytes = numToBytes(this._blockData.payloadSize, cc.PAYLOAD_SIZE_BYTES);
      const encFlags = numToBytes(this._blockData.flags!, cc.FLAGS_BYTES);

      const sodium = getSodium();
      const sk = await this._keyProvider.getSigningKey();
      const state = sodium.crypto_generichash_init(sk, cc.MAC_BYTES);

      sodium.crypto_generichash_update(state, encVer);
      sodium.crypto_generichash_update(state, encSizeBytes);
      sodium.crypto_generichash_update(state, encFlags);
      sodium.crypto_generichash_update(state, this._blockData.additionalData);
      sodium.crypto_generichash_update(state, this._blockData.encryptedData);
      sodium.crypto_generichash_update(state, this._lastMac);

      const testMac = sodium.crypto_generichash_final(state, cc.MAC_BYTES);
      const validMac: boolean = sodium.memcmp(this._blockData.mac, testMac);

      if (validMac) {
         this._lastMac = testMac;
         return;
      }

      throw new Error('Invalid MAC signature');
   }
}
